import type { GenerationAssumptionEvidenceRef } from '../../types/generationImpact';
import type { SiteEntity } from '../../types/site';
import type { DocumentEvidenceFact, SiteEvidenceDocument } from '../../types/siteEvidence';
import type { ProjectPhase, SiteDocument, SiteRagReport } from '../../types/siteRag';
import { generationImpactScenarioIntegrityReason } from '../generationImpact/integrity';
import { resolvePromotedGenerationScenario } from '../generationImpact/promotedScenario';
import { resolveGenerationImpactSedAuthority } from '../generationImpact/sedAuthority';
import { sha256Hex } from '../reportGeneration/reportContractHash';

/** Live owner-loaded authority, separate from the report's historical SED. */
export type PowerScoringAuthorityContext = Pick<SiteEntity, 'id' | '_ownerUid' | 'createdBy'
  | 'sedId' | 'sedVersion' | 'sedStatus' | 'sedBootstrapLockStatus' | 'siteEvidenceDirtySections'
  | 'projectIntent' | 'documents' | 'promotedGenerationScenarioSnapshot'
  | 'generationAssessmentSelection' | 'generationProjectInputBinding'>;

export interface AllocatedServiceEvidence {
  allocatedServiceMW: number;
  facilityDemandMW: number;
  reserveMarginPct: number;
  /** Arithmetic over the admitted allocation and facility demand, never ATC. */
  designLoadMarginMW: number;
  phaseId: string;
  serviceDate: string;
  statedOn: string;
  documentName: string;
  documentId: string;
  referenceNumber: number;
  confidence: number;
}

const SHA = /^[a-f\d]{64}$/i;
const DATE = /^\d{4}-\d{2}-\d{2}$/;
const NUMBER = '((?:\\d{1,3}(?:,\\d{3})+|\\d+)(?:\\.\\d+)?)';
const allocationPattern = new RegExp(`^(?:The\\s+)?(?:(?:project|site[- ]deliverable|source[- ]stated|site|firm|issued|offered)\\s+)*load[- ]service\\s+allocation\\s+is\\s+${NUMBER}\\s*MW[.]?$`, 'i');
const demandPattern = new RegExp(`^(?:The\\s+)?facility\\s+demand\\s+is\\s+${NUMBER}\\s*MW(?:\\s+including\\s+auxiliaries\\s+and\\s+cooling)?[.]?$`, 'i');
const numeric = (value: string) => Number(value.replace(/,/g, ''));
const normalize = (value: string) => value.replace(/\s+/g, ' ').trim();
const dateIsValid = (value: string) => DATE.test(value) && Number.isFinite(Date.parse(value))
  && new Date(value).toISOString().slice(0, 10) === value;

/** Source calendar dates are independent of the browser/server timezone. */
function sourceDate(value: string): string | null {
  if (dateIsValid(value)) return value;
  const parts = /^(\d{1,2})\s+([A-Za-z]+)\s+(\d{4})$/.exec(value);
  if (!parts) return null;
  const months = ['january', 'february', 'march', 'april', 'may', 'june', 'july', 'august', 'september', 'october', 'november', 'december'];
  const month = months.findIndex(name => name === parts[2]!.toLowerCase() || name.slice(0, 3) === parts[2]!.toLowerCase());
  if (month < 0) return null;
  const result = new Date(Date.UTC(Number(parts[3]), month, Number(parts[1]))).toISOString().slice(0, 10);
  return Number(result.slice(8)) === Number(parts[1]) ? result : null;
}

function samePhase(left: ProjectPhase, right: ProjectPhase): boolean {
  return left.id === right.id && left.sequence === right.sequence && left.capacityMW === right.capacityMW
    && left.targetInServiceDate === right.targetInServiceDate && left.powerSource === right.powerSource;
}

function quotedFact(fact: DocumentEvidenceFact, doc: SiteDocument): boolean {
  return fact.adoption === 'source-statement' && fact.channel === 'document'
    && Number.isFinite(fact.confidence) && fact.confidence > 0 && fact.confidence <= 1
    && Number.isSafeInteger(fact.charStart) && Number.isSafeInteger(fact.charEnd)
    && fact.charStart >= 0 && fact.charEnd > fact.charStart
    && fact.charEnd <= doc.fullExtractedText!.length
    && normalize(doc.fullExtractedText!.slice(fact.charStart, fact.charEnd)) === normalize(fact.sourceQuote)
    && Number.isSafeInteger(fact.page) && (fact.page ?? 0) >= 1
    && doc.pages?.some(page => page.page === fact.page && fact.charStart >= page.charStart && fact.charEnd <= page.charEnd) === true;
}

function bindsDocument(ref: GenerationAssumptionEvidenceRef, doc: SiteDocument, fact: DocumentEvidenceFact): boolean {
  return ref.kind === 'document' && ref.documentId === doc.id && ref.documentSha256 === doc.contentHash
    && Boolean(ref.factId && ref.extractionVersion) && ref.page === fact.page
    && ref.sourceOffset?.start === fact.charStart && ref.sourceOffset.end === fact.charEnd
    && normalize(ref.excerpt ?? '') === normalize(fact.sourceQuote);
}

/** A distinct source-stated planning comparison. A passing GI result, utility
 * name, prose claim, or manual allocation cannot establish this signal. The
 * selected full source bytes and both current and frozen scenario identities
 * must agree before arithmetic is permitted. Delivery and commissioning are
 * still conditions; this never supplies measured utility headroom or ATC. */
function resolveSource(args: {
  report: SiteRagReport;
  sed?: SiteEvidenceDocument | null;
  live?: PowerScoringAuthorityContext;
  facilityLoadMW?: number | null;
}): AllocatedServiceEvidence | null {
  const { report, sed, live } = args;
  const intent = live?.projectIntent;
  const owner = live?._ownerUid ?? live?.createdBy?.uid;
  if (!sed || !live || !owner || report._metadataOnly || report.siteId !== live.id
    || live.sedId !== sed.id || live.sedVersion !== sed.version
    || live.sedStatus !== 'complete' || live.sedBootstrapLockStatus === 'locked'
    || live.siteEvidenceDirtySections?.length || !intent
    || !intent.promotedGenerationInputHash || !intent.promotedGenerationResultHash
    || !intent.promotedGenerationPromotedAt || typeof args.facilityLoadMW !== 'number'
    || !Number.isFinite(args.facilityLoadMW) || args.facilityLoadMW <= 0) return null;
  const resolved = resolveGenerationImpactSedAuthority(sed);
  if (resolved.status !== 'valid') return null;
  const current = resolvePromotedGenerationScenario(intent, live.promotedGenerationScenarioSnapshot, null,
    live.generationAssessmentSelection, live.generationProjectInputBinding);
  const frozen = report.generationImpactScenarioAtGen;
  if (!current || !frozen || generationImpactScenarioIntegrityReason(current)
    || generationImpactScenarioIntegrityReason(frozen) || current.siteId !== live.id
    || current.promotedAt !== intent.promotedGenerationPromotedAt) return null;
  const sameScenario = (other: typeof current) => other.id === current.id && other.siteId === current.siteId
    && other.inputHash === current.inputHash && other.resultHash === current.resultHash
    && other.updatedAt === current.updatedAt && other.promotedAt === current.promotedAt;
  if (!sameScenario(frozen) || !sameScenario(resolved.snapshot.scenarioSnapshot)) return null;
  const phaseId = current.targetPhaseId;
  const livePhases = intent.phases?.filter(phase => phase.id === phaseId) ?? [];
  const sedPhases = sed.fullProjectIntent?.phases?.filter(phase => phase.id === phaseId) ?? [];
  const reportPhases = report.projectIntentAtGen?.phases?.filter(phase => phase.id === phaseId) ?? [];
  if (!phaseId || livePhases.length !== 1 || sedPhases.length !== 1 || reportPhases.length !== 1) return null;
  const phase = livePhases[0]!;
  if (!samePhase(phase, sedPhases[0]!) || !samePhase(phase, reportPhases[0]!)
    || intent.powerBasis !== 'facility' || sed.fullProjectIntent?.powerBasis !== 'facility'
    || report.projectIntentAtGen?.powerBasis !== 'facility'
    || phase.capacityMW !== current.input.facilityLoadMW || args.facilityLoadMW !== current.input.facilityLoadMW
    || typeof current.input.reserveMarginPct !== 'number'
    || !Number.isFinite(current.input.reserveMarginPct) || current.input.reserveMarginPct < 0
    || !phase.targetInServiceDate
    || !dateIsValid(phase.targetInServiceDate) || phase.powerSource !== 'grid'
    || current.input.gridConnection !== 'grid-connected'
    || !current.input.units.length || current.input.units.some(unit => unit.technology !== 'grid-import')) return null;
  const at = Date.parse(sed.collectedAt), generatedAt = Date.parse(report.createdAt);
  if (!Number.isFinite(at) || !Number.isFinite(generatedAt)) return null;
  const candidates: AllocatedServiceEvidence[] = [];
  for (const [index, ref] of (report.references ?? []).entries()) {
    if (ref.field !== 'documents' || !ref.documentId || ref.url !== `doc://${ref.documentId}` || ref.needsVerification
      || (report.references ?? []).filter(row => row.documentId === ref.documentId && row.field === 'documents').length !== 1
      || !report.documentIds?.includes(ref.documentId)) continue;
    const selections = report.documentRefs?.filter(row => row.docId === ref.documentId) ?? [];
    if (selections.length > 1 || selections.some(row => Boolean(row.reasonIfExcluded)
      || (row.userIncluded === false && report.documentSelectionMode !== 'auto'))) continue;
    const selected = report.documents?.filter(doc => doc.id === ref.documentId) ?? [];
    const liveDocuments = live.documents?.filter(doc => doc.id === ref.documentId) ?? [];
    if (selected.length !== 1 || liveDocuments.length !== 1) continue;
    const doc = selected[0]!, currentDoc = liveDocuments[0]!;
    if (!doc.fullExtractedText || doc.extractedCharsKept !== doc.fullExtractedText.length
      || !SHA.test(doc.contentHash ?? '') || currentDoc.contentHash !== doc.contentHash
      || doc.includeInSed === false || currentDoc.includeInSed === false
      || doc.storagePath !== `users/${owner}/documents/${doc.id}.json`
      || currentDoc.storagePath !== doc.storagePath
      || sed.siteContext?.docContextScans?.[doc.id]?.bodyHash !== sha256Hex(doc.fullExtractedText)
      || (currentDoc.fullExtractedText != null && sha256Hex(currentDoc.fullExtractedText) !== sha256Hex(doc.fullExtractedText))) continue;
    const body = normalize(doc.fullExtractedText);
    const phaseTokens = [...body.matchAll(/\bselected\s+phase\s+([A-Za-z\d][A-Za-z\d_-]*)\b/gi)];
    if (phaseTokens.length !== 1 || phaseTokens[0]![1] !== phaseId
      || /\b(?:allocation|load[- ]service\s+agreement)\b[^.!?]{0,160}\b(?:withdrawn|superseded|invalid|rejected|unknown)\b/i.test(body)) continue;
    const statedAllocations = [...body.matchAll(new RegExp(`\\bload[- ]service\\s+allocation\\s+is\\s+${NUMBER}\\s*MW`, 'gi'))];
    const statedDemands = [...body.matchAll(new RegExp(`\\bfacility\\s+demand\\s+is\\s+${NUMBER}\\s*MW`, 'gi'))];
    const sourceSentences = body.split(/[.!?](?=\s|$)|[;\n|]/);
    const agreementStatements = sourceSentences.filter(row => /\bload[- ]service\s+agreement\b/i.test(row));
    // An issue/effective date by itself does not turn a draft offer into an
    // issued service agreement. Delivery conditions remain separately stated.
    if (!agreementStatements.some(row => /\bissued\s+load[- ]service\s+agreement\b|\bload[- ]service\s+agreement\b[^.!?]{0,80}\b(?:is|was|has\s+been)\s+issued\b/i.test(row))
      || agreementStatements.some(row => /\b(?:draft|proposed|proposal|unissued|non[- ]issued|conditional(?:ly)?|if|unless|would)\b|\b(?:not\s+(?:an?\s+)?(?:yet\s+)?issued|no\s+issued)\b/i.test(row))) continue;
    const serviceDates = [...body.matchAll(/\bservice\s+(?:commencement|start(?:\s+date)?|date)\s+(?:(?:is|on)\s+)?(\d{4}-\d{2}-\d{2}|\d{1,2}\s+[A-Za-z]+\s+\d{4})\b/gi)]
      .map(match => sourceDate(match[1]!));
    if (sourceSentences.some(row => /\b(?:load[- ]service\s+allocation|facility\s+demand\s+is)\b/i.test(row)
      && /\b(?:not|no|if|unless|unknown|withdrawn|superseded|rejected)\b/i.test(row))) continue;
    const facts = (sed.documentFacts ?? []).filter(fact => fact.documentId === doc.id && quotedFact(fact, doc));
    const allocations = facts.filter(fact => fact.unit === 'MW' && allocationPattern.test(normalize(fact.sourceQuote)));
    const demands = facts.filter(fact => fact.unit === 'MW' && demandPattern.test(normalize(fact.sourceQuote)));
    const services = facts.filter(fact => dateIsValid(fact.value)
      && /\bservice\s+(?:commencement|start(?:\s+date)?|date)\s+(?:(?:is|on)\s+)?\d{4}-\d{2}-\d{2}\b/i.test(fact.sourceQuote));
    if (allocations.length !== 1 || demands.length !== 1 || services.length !== 1) continue;
    const allocation = allocations[0]!, demand = demands[0]!, service = services[0]!;
    const allocatedServiceMW = numeric(allocation.value), facilityDemandMW = numeric(demand.value);
    const statedOn = allocation.attribution?.statedOn;
    if (!Number.isFinite(allocatedServiceMW) || allocatedServiceMW <= 0 || !Number.isFinite(facilityDemandMW)
      || facilityDemandMW <= 0 || allocatedServiceMW !== numeric(allocationPattern.exec(normalize(allocation.sourceQuote))![1]!)
      || facilityDemandMW !== numeric(demandPattern.exec(normalize(demand.sourceQuote))![1]!)
      || !statedAllocations.length || statedAllocations.some(match => numeric(match[1]!) !== allocatedServiceMW)
      || !statedDemands.length || statedDemands.some(match => numeric(match[1]!) !== facilityDemandMW)
      || facilityDemandMW !== current.input.facilityLoadMW || !statedOn || !dateIsValid(statedOn)
      || !allocation.attribution?.organisation || !allocation.attribution?.statedByRole
      || demand.attribution?.statedOn !== statedOn || service.attribution?.statedOn !== statedOn
      || Date.parse(statedOn) > at || Date.parse(statedOn) > generatedAt
      || service.value !== phase.targetInServiceDate || !service.sourceQuote.includes(service.value)
      || !serviceDates.length || serviceDates.some(date => date !== service.value)
      || Date.parse(service.value) < Date.parse(statedOn)
      || /\b(?:not|no|unknown|withdrawn|superseded|rejected)\b/i.test(service.sourceQuote)
      || !body.includes(normalize(allocation.attribution.organisation))) continue;
    const issueDates = [...body.matchAll(/\b(?:issued\s+and\s+effective|issued|effective)\s+(?:on\s+)?(\d{4}-\d{2}-\d{2}|\d{1,2}\s+[A-Za-z]+\s+\d{4})\b/gi)]
      .map(match => sourceDate(match[1]!));
    if (!issueDates.length || issueDates.some(date => date !== statedOn)) continue;
    const demandAssumptions = current.input.assumptions.filter(assumption => assumption.field === 'facilityLoadMW');
    if (demandAssumptions.length !== 1 || demandAssumptions[0]!.source !== 'document'
      || demandAssumptions[0]!.value !== facilityDemandMW
      || !demandAssumptions[0]!.evidenceRefs?.some(evidence => bindsDocument(evidence, doc, demand))) continue;
    candidates.push({ allocatedServiceMW, facilityDemandMW, reserveMarginPct: current.input.reserveMarginPct,
      designLoadMarginMW: allocatedServiceMW - facilityDemandMW,
      phaseId, serviceDate: service.value, statedOn, documentName: doc.fileName, documentId: doc.id,
      referenceNumber: index + 1, confidence: Math.min(0.6, allocation.confidence, demand.confidence, service.confidence) });
  }
  // Two competing agreements never supply a blended or cherry-picked signal.
  return candidates.length === 1 ? candidates[0]! : null;
}

/** Missing or malformed authority withholds this optional signal, never the
 * independent provider/queue evidence scored by the caller. */
export function resolveAllocatedServiceEvidence(args: {
  report: SiteRagReport;
  sed?: SiteEvidenceDocument | null;
  live?: PowerScoringAuthorityContext;
  facilityLoadMW?: number | null;
}): AllocatedServiceEvidence | null {
  try { return resolveSource(args); } catch { return null; }
}
